/**
 * Combat Feedback Config
 *
 * Visual + threshold tuning for damage numbers, hit flashes, crit highlight,
 * and impact intensity buckets. Defines what damage looks like — the
 * server→client school mapping (`SCHOOL_TO_FEEDBACK_TYPE`) lives in the
 * legacy file because it's structural, not visual tuning.
 *
 * Two-section file (defaults+dev-override pattern):
 *   1. COMBAT_FEEDBACK_DEFAULTS — immutable production values.
 *   2. COMBAT_FEEDBACK_DEV_OVERRIDE — Partial<>; tweak here to iterate
 *      (e.g. `criticalHitColor: '#ff00ff'` for QA visibility).
 *
 * Consumers import `COMBAT_FEEDBACK` (the merged result).
 *
 * Pattern docs: `docs/plans/2026-04-25-CONFIG-MIGRATION.md`.
 */

export interface CombatFeedbackConfig {
  /** Floating damage-number color per ability school (hex string for DOM/Phaser text). */
  damageTypeColors: Record<string, string>;
  /** Same palette but as 0xRRGGBB integers for Phaser sprite tints. */
  damageTypeTints: Record<string, number>;
  /** Color used for critical-hit damage numbers — overrides the school color. */
  criticalHitColor: string;
  /**
   * Colorblind-safe floating damage-number palette (Okabe-Ito-based). Swapped in
   * when getMotionPrefs().colorblindSafe is on, so red/green schools (and heal vs
   * incoming damage) stay distinguishable for deuteranopia/protanopia.
   */
  damageTypeColorsColorblind: Record<string, string>;
  /** Critical-hit color under the colorblind-safe palette. */
  criticalHitColorColorblind: string;
  /**
   * Damage thresholds that pick the impact-feedback bucket.
   * Order: anything > heavy is "massive"; > medium is "heavy";
   * > light is "medium"; otherwise "light".
   */
  impactIntensity: {
    light: number;
    medium: number;
    heavy: number;
  };
}

export const COMBAT_FEEDBACK_DEFAULTS: CombatFeedbackConfig = {
  damageTypeColors: {
    physical: '#FF0000',
    fire: '#FF6600',
    frost: '#66CCFF',
    arcane: '#CC66FF',
    shadow: '#9933CC',
    holy: '#FFDD00',
    nature: '#33CC33',
    chaos: '#FF0066',
    various: '#FFFFFF',
  },
  damageTypeTints: {
    physical: 0xff6b35,
    fire: 0xff0000,
    frost: 0x4cc9f0,
    arcane: 0x553c9a,
    shadow: 0x9d4edd,
    holy: 0xffd700,
    nature: 0x33cc33,
    lightning: 0xf9c74f,
    chaos: 0xff0066,
    various: 0xffffff,
  },
  criticalHitColor: '#FFD700',
  // Okabe-Ito colorblind-safe palette — distinguishable across the common CVD
  // types (no red/green ambiguity). First-pass; tune for feel.
  damageTypeColorsColorblind: {
    physical: '#E69F00', // orange (was red)
    fire: '#D55E00',     // vermillion
    frost: '#56B4E9',    // sky blue
    arcane: '#CC79A7',   // reddish purple
    shadow: '#0072B2',   // blue (was purple)
    holy: '#F0E442',     // yellow
    nature: '#009E73',   // bluish green (was red-ambiguous green)
    chaos: '#CC79A7',    // mauve (rare; shares with arcane)
    various: '#FFFFFF',
  },
  criticalHitColorColorblind: '#FFFFFF',
  impactIntensity: {
    light: 25,
    medium: 50,
    heavy: 100,
  },
};

/**
 * Per-dev-environment overrides. Partial — anything omitted falls through
 * to COMBAT_FEEDBACK_DEFAULTS.
 *
 * Examples:
 *   criticalHitColor: '#ff00ff'                              // hot-pink crit for QA
 *   impactIntensity: { light: 5, medium: 15, heavy: 30 }     // exaggerated buckets
 */
export const COMBAT_FEEDBACK_DEV_OVERRIDE: Partial<CombatFeedbackConfig> = {};

export const COMBAT_FEEDBACK: CombatFeedbackConfig = {
  damageTypeColors: { ...COMBAT_FEEDBACK_DEFAULTS.damageTypeColors, ...COMBAT_FEEDBACK_DEV_OVERRIDE.damageTypeColors },
  damageTypeTints: { ...COMBAT_FEEDBACK_DEFAULTS.damageTypeTints, ...COMBAT_FEEDBACK_DEV_OVERRIDE.damageTypeTints },
  criticalHitColor: COMBAT_FEEDBACK_DEV_OVERRIDE.criticalHitColor ?? COMBAT_FEEDBACK_DEFAULTS.criticalHitColor,
  damageTypeColorsColorblind: { ...COMBAT_FEEDBACK_DEFAULTS.damageTypeColorsColorblind, ...COMBAT_FEEDBACK_DEV_OVERRIDE.damageTypeColorsColorblind },
  criticalHitColorColorblind: COMBAT_FEEDBACK_DEV_OVERRIDE.criticalHitColorColorblind ?? COMBAT_FEEDBACK_DEFAULTS.criticalHitColorColorblind,
  impactIntensity: { ...COMBAT_FEEDBACK_DEFAULTS.impactIntensity, ...COMBAT_FEEDBACK_DEV_OVERRIDE.impactIntensity },
};

/**
 * Floating damage-number color for an NPC/target hit. Normal mode is byte-identical
 * to the legacy `isCrit ? criticalHitColor : damageTypeColors[school] ?? '#FF0000'`;
 * colorblindSafe swaps to the CB palette.
 */
export function resolveDamageColor(school: string, isCrit: boolean, colorblindSafe: boolean): string {
  if (colorblindSafe) {
    return isCrit
      ? COMBAT_FEEDBACK.criticalHitColorColorblind
      : (COMBAT_FEEDBACK.damageTypeColorsColorblind[school] ?? COMBAT_FEEDBACK.damageTypeColorsColorblind.physical);
  }
  return isCrit ? COMBAT_FEEDBACK.criticalHitColor : (COMBAT_FEEDBACK.damageTypeColors[school] ?? '#FF0000');
}

/**
 * Floating damage-number color for the local/remote player being hit ("you got
 * hit"). Normal mode keeps the legacy reds; colorblindSafe routes off red so it's
 * not confused with green heals.
 */
export function resolveIncomingDamageColor(isCrit: boolean, colorblindSafe: boolean): string {
  if (colorblindSafe) {
    return isCrit ? COMBAT_FEEDBACK.criticalHitColorColorblind : COMBAT_FEEDBACK.damageTypeColorsColorblind.physical;
  }
  return isCrit ? '#FF00A0' : '#FF5555';
}

/**
 * Floating-text style color (decimal int) for FloatingTextManager. Normal mode is
 * byte-identical to the legacy switch; colorblindSafe moves heal off green and
 * damage off red while keeping them distinct. Returns null for styles the manager
 * colors itself (info/xp), so the caller keeps its own value.
 */
export function resolveFloatingTextColor(style: string | undefined, colorblindSafe: boolean): number | null {
  const normal: Record<string, number> = { damage: 0xff0000, heal: 0x00ff00, critical: 0xffaa00, ability: 0xaa00ff };
  const cb: Record<string, number> = { damage: 0xe69f00, heal: 0x56b4e9, critical: 0xffffff, ability: 0xcc79a7 };
  const table = colorblindSafe ? cb : normal;
  if (!style || !(style in table)) return null;
  return table[style];
}

/**
 * Floating damage-number color for healing.
 * Normal mode is green (#00ff00); colorblindSafe moves heal off green to blue (#56b4e9).
 */
export function resolveHealColor(colorblindSafe: boolean): string {
  return colorblindSafe ? '#56b4e9' : '#00ff00';
}
